/**
 * Summary page — shows dialog results after evaluation.
 *
 * Laid out as a marked report: a score box with the pass verdict, a
 * candidate banner (name + student number), and an objective table with
 * feedback, gaps, misconceptions and arithmetic slips.
 */
import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import type { DialogSummary } from "../../types";
import { MathText } from "../../components/Math";

function scoreClassFor(pct: number) {
  return pct >= 70 ? "score-good" : pct >= 50 ? "score-mid" : "score-low";
}

export function SummaryPage() {
  const { cid, sid } = useParams<{ cid: string; sid: string }>();
  const { user } = useAuth();
  const [summary, setSummary] = useState<DialogSummary | null>(null);
  const [lessonId, setLessonId] = useState("");

  useEffect(() => {
    const raw = sessionStorage.getItem("dialog_results");
    const lid = sessionStorage.getItem("dialog_lesson_id") || "";
    if (raw) {
      try {
        setSummary(JSON.parse(raw));
        setLessonId(lid);
      } catch {
        // ignore parse error
      }
    }
  }, []);

  if (!summary) {
    return (
      <div className="summary-page">
        <div className="card text-center">
          <h2>No Assessment Results Found</h2>
          <p className="text-muted">
            These results are no longer in this browser session. Your progress is saved — see My Progress.
          </p>
          <Link to={`/learn/${cid}`} className="btn btn-secondary" style={{ marginTop: 16 }}>
            Back to Curriculum Pathway
          </Link>
        </div>
      </div>
    );
  }

  const scorePct = Math.round(
    summary.overall_score > 1 ? summary.overall_score : summary.overall_score * 100
  );

  const misconceptions = summary.objective_scores.flatMap((os) =>
    (os.misconceptions || []).map((m) => ({ objective: os.objective, text: m }))
  );
  const calculationErrors = summary.objective_scores.flatMap((os) =>
    (os.calculation_errors || []).map((c) => ({ objective: os.objective, text: c }))
  );

  return (
    <div className="summary-page">
      <div className="report-memorandum">
        {/* Memorandum Header with Examiner Mark Box */}
        <header className="memorandum-header">
          <div className="memorandum-title-area">
            <h1>Dialogue Results</h1>

            <div className="candidate-info-line">
              <span className="info-label">Candidate:</span>
              <strong>{user?.name || "Learner"}</strong>
              {user?.student_number && (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="candidate-number">Student No: {user.student_number}</span>
                </>
              )}
              <span aria-hidden="true">·</span>
              <span className="assessment-date">{new Date().toLocaleDateString("en-ZA")}</span>
            </div>

            {summary.summary && <p className="memorandum-overview">{summary.summary}</p>}
          </div>

          {/* Examiner Score Box */}
          <div className={`examiner-mark-box${summary.ready_to_advance ? " mark-box-pass" : ""}`}>
            <span className="mark-box-label">SCORE</span>
            <div className="mark-box-value">{scorePct}%</div>
            <div className={`mark-box-verdict ${summary.ready_to_advance ? "verdict-pass" : "verdict-review"}`}>
              {summary.ready_to_advance ? "✓ PASSED (ADVANCE)" : "⚠ REVISION REQUIRED"}
            </div>
          </div>
        </header>

        {!!summary.marks_awaiting_instructor && (
          <p className="provisional-note">
            {summary.marks_awaiting_instructor === 1
              ? "One of your answers was marked differently by the markers, so your teacher will decide it."
              : `${summary.marks_awaiting_instructor} of your answers were marked differently by the markers, so your teacher will decide them.`}{" "}
            Marks labelled provisional may change, and your progress updates when they do.
          </p>
        )}

        {/* Detailed Rubric Criteria Table */}
        <div className="report-scroll">
          <table className="rubric">
            <thead>
              <tr>
                <th className="rubric-criterion">Assessment Criterion / Objective</th>
                <th className="rubric-mark">Mark</th>
                <th>Examiner Feedback &amp; Diagnostic Notes</th>
              </tr>
            </thead>
            <tbody>
              {summary.objective_scores.map((os, i) => {
                const pct = Math.round(os.score > 1 ? os.score : os.score * 100);
                return (
                  <tr key={i}>
                    <td className="rubric-criterion">
                      <strong>{os.objective}</strong>
                    </td>
                    <td className={`rubric-mark ${scoreClassFor(pct)}`}>
                      {pct}%
                      {os.provisional && <span className="provisional-tag">provisional</span>}
                    </td>
                    <td>
                      {os.feedback && <p className="feedback-text">{os.feedback}</p>}
                      {os.gaps && os.gaps.length > 0 && (
                        <p className="rubric-gaps">
                          <span>Concept Gap:</span> {os.gaps.join(", ")}
                        </p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className="rubric-criterion">Overall Score</td>
                <td className={`rubric-mark ${scoreClassFor(scorePct)}`}>{scorePct}%</td>
                <td>
                  <strong>
                    {summary.ready_to_advance
                      ? "Competency demonstrated. Ready to proceed to subsequent curriculum units."
                      : "Core competencies require reinforcement before moving forward."}
                  </strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Strengths & Observations */}
      {summary.strengths.length > 0 && (
        <section className="card report-section-block">
          <h3>Demonstrated Strengths</h3>
          <ul className="observations-list">
            {summary.strengths.map((s, i) => (
              <li key={i}>
                <span className="bullet-check">✓</span> {s}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Misconceptions */}
      {misconceptions.length > 0 && (
        <section className="card report-section-block report-section-flagged">
          <h3>Specific Misconceptions Identified</h3>
          <ul className="observations-list">
            {misconceptions.map((m, i) => (
              <li key={i}>
                <span className="bullet-alert">⚠</span>
                <div>
                  <strong>{m.objective}:</strong> <MathText inline content={m.text} />
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Arithmetic / Calculation */}
      {calculationErrors.length > 0 && (
        <section className="card report-section-block report-section-flagged">
          <h3>Arithmetic &amp; Working Corrections</h3>
          <ul className="observations-list">
            {calculationErrors.map((c, i) => (
              <li key={i}>
                <span className="bullet-alert">⚠</span>
                <div>
                  <strong>{c.objective}:</strong> <MathText inline content={c.text} />
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Recommendations */}
      {summary.recommendations.length > 0 && (
        <section className="card report-section-block">
          <h3>Targeted Learning Recommendations</h3>
          <div className="recommendations-grid">
            {summary.recommendations.map((r, i) => (
              <div key={i} className="recommendation-item">
                <span className="recommendation-area">{r.area}</span>
                <p className="recommendation-suggestion">{r.suggestion}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Action Footer */}
      <div className="summary-actions-footer">
        <Link to={`/learn/${cid}`} className="btn btn-secondary">
          ← Return to Learning Roadmap
        </Link>
        {lessonId && (
          <Link to={`/learn/${cid}/dialog/${lessonId}`} className="btn btn-primary">
            Retake Dialogue →
          </Link>
        )}
      </div>
    </div>
  );
}
